import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './Sorting.module.css';
import { generateUnsortedArray } from '../../lib/arrayGenerator';

const delay = (ms: number) => new Promise(res => setTimeout(res, ms));

const BUBBLE_SORT_CODE = `function bubbleSort(arr) {
  let n = arr.length;
  for (let i = 0; i < n - 1; i++) {
    for (let j = 0; j < n - i - 1; j++) {
      if (arr[j] > arr[j + 1]) {
        [arr[j], arr[j + 1]] = [arr[j + 1], arr[j]];
      }
    }
  }
}`;

const BubbleSort: React.FC = () => {
  const navigate = useNavigate();
  const [array, setArray] = useState<number[]>([]);
  const [isSorting, setIsSorting] = useState(false);
  const [highlightedIndices, setHighlightedIndices] = useState<number[]>([]);
  const [sortedIndices, setSortedIndices] = useState<number[]>([]);
  const [highlightedLine, setHighlightedLine] = useState<number | null>(null);
  const stopSort = useRef(false);

  useEffect(() => {
    handleRandomize();
    return () => {
      stopSort.current = true;
    };
  }, []);

  const handleGoBack = () => navigate(-1);

  const stop = () => {
    stopSort.current = true;
    setIsSorting(false);
    setHighlightedIndices([]);
    setSortedIndices([]);
    setHighlightedLine(null);
  };

  const handleRandomize = () => {
    stop();
    setArray(generateUnsortedArray(15, 100));
  };

  const runBubbleSort = async () => {
    stopSort.current = false;
    setIsSorting(true);
    setHighlightedIndices([]);
    setSortedIndices([]);
    setHighlightedLine(null);

    let arr = [...array];
    let n = arr.length;

    for (let i = 0; i < n - 1; i++) {
      setHighlightedLine(3);
      if (stopSort.current) return setIsSorting(false);

      for (let j = 0; j < n - i - 1; j++) {
        setHighlightedLine(4);
        setHighlightedIndices([j, j + 1]);
        if (stopSort.current) return setIsSorting(false);
        await delay(250);

        setHighlightedLine(5);
        if (arr[j] > arr[j + 1]) {
          if (stopSort.current) return setIsSorting(false);
          await delay(250);

          setHighlightedLine(7);
          [arr[j], arr[j + 1]] = [arr[j + 1], arr[j]];
          setArray([...arr]);
          if (stopSort.current) return setIsSorting(false);
          await delay(300);
        }
      }
      setSortedIndices(prev => [...prev, n - 1 - i]);
    }

    if (!stopSort.current) {
      setSortedIndices(Array.from(Array(n).keys()));
    }
    setHighlightedIndices([]);
    setHighlightedLine(null);
    setIsSorting(false);
  };

  return (
    <div className={styles.container}>
      <button onClick={() => navigate(-1)} className={styles.returnButton}>
        ← Return
      </button>
      <h1 className={styles.title}>Bubble Sort</h1>

        <div className={styles.description}>
          <h2>Description</h2>
          <p>Bubble Sort is a simple sorting algorithm that repeatedly steps through the list, compares adjacent elements, and swaps them if they are in the wrong order.</p>
        </div>
        <div className={styles.useCase}>
          <h3>When to use it?</h3>
          <ul>
            <li>When the data set is very small.</li>
          </ul>
        </div>
        <div className={styles.timeComplexity}>
          <h3>Time Complexity</h3>
          <p>
            <b>Best Case:</b> O(n)
            <br />
            <b>Average/Worst Case:</b> O(n²)
          </p>
        </div>
 
      <div className={styles.visualization}>
        {array.map((value, index) => (
          <div
            key={index}
            className={`
              ${styles.bar}
              ${highlightedIndices.includes(index) ? styles.comparing : ''}
              ${sortedIndices.includes(index) ? styles.sorted : ''}
            `}
            style={{ height: `${value * 3.5}px` }}
          >
            <span className={styles.barValue}>{value}</span>
          </div>
        ))}
      </div>

      <div className={styles.controls}>
        <button onClick={runBubbleSort} disabled={isSorting}>Run Bubble Sort</button>
        <button onClick={handleRandomize} disabled={isSorting}>Randomize</button>
        <button onClick={stop} disabled={!isSorting}>Stop</button>
      </div>

      <div className={styles.codeDisplay}>
        {BUBBLE_SORT_CODE.split('\n').map((line, index) => (
          <div key={index} className={`${styles.codeLine} ${highlightedLine === index + 1 ? styles.highlightedLine : ''}`}>
            {line}
          </div>
        ))}
      </div>
    </div>
  );
};

export default BubbleSort;